import React, { useContext, useEffect, useState } from 'react'
import { MemberTableType, MemberTableData } from '../data/MemberTableData'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import MicroModal from 'micromodal'
import { getExportHeaders } from '../../ReportingGroup/types/tableHeaders'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { UserBadge, modalMembersTblBtnId, notAllowedUserMembershipStatus, modalManageUserId, modalConfirmationMessageId } from './Utils'
import ModalConfirmation from './ModalConfirmation'
import AddMemberModal from '../form/AddMemberModal'
import { useParams } from 'react-router-dom'
import { ColumnDef, ColumnFiltersState, SortingState } from '@tanstack/react-table' 
import ModalManageUser from './ManageUser/ModalManageUser'
import { TeamContext } from '../../MenuTabs/MenuTabs'
import ModalInviteLink from '../PendingInvites/ModalInviteLink'
import useMemberTable from '../context/MemberTableContext'
import BulkModalConfirmation from '../Header/Actions/BulkModalConfirmation'
import { customFilterFn } from '../Header/Utils'
import MembersTableButton from './MembersTableButton'

const MembersTable = ({ membersData }) => {

  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([])
  const [ rowSelection, setRowSelection ] = React.useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => MemberTableData( membersData ) )
  const { setUseHeader, refetchMembers } = useMemberTable()

  const params = useParams()
  const teamId = params.team_id ?? 0

  useEffect(() => {
    MicroModal.init()
    setUseHeader(['all'])
  }, [])

  const columns = React.useMemo<ColumnDef<MemberTableType>[]>(
    () => [
      {     
        id: 'select',
        meta: {
          className: "msync-table__cell--select",
        },
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'teamId',
            accessorKey: 'teamId',
            prettyName: 'Team ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Team ID</span>,
          },
          {
            id: 'userId',
            accessorKey: 'userId',
            prettyName: 'User ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>User ID</span>,
          },
          {
            id: 'avatarUrl',
            accessorKey: 'avatarUrl',
            prettyName: 'Avatar Url',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Avatar URL</span>,
          },
          {
            id: 'email',
            accessorKey: 'email',
            prettyName: 'Email',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Email</span>,
          },
          {
            id: 'lastActivity',
            accessorKey: 'lastActivity',
            prettyName: 'Last Activity',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Last Activity</span>,
          },
          {
            id: 'firstName',
            accessorKey: 'firstName',
            prettyName: 'First Name',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>First Name</span>,
          },
          {
            id: 'lastName',
            accessorKey: 'lastName',
            prettyName: 'Last Name',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Last Name</span>,
          },
          {
            id: 'displayName',
            accessorKey: 'displayName',
            prettyName: 'Display Name',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Display Name</span>,
          },
          {
            id: 'user',
            accessorKey: 'user',
            prettyName: 'User',
            cell: (info) => (
              <User info={ info.row.original } />
            ),
            footer: props => props.column.id,
            header: () => <span>User</span>,
          },
          {
            id: 'username',
            accessorKey: 'username',
            prettyName: 'Username',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Username</span>,
          },
          {
            id: 'role',
            accessorKey: 'role',
            prettyName: 'Role',
            cell: (info) => (
              <UserBadge role={ info.getValue() } />
            ),
            footer: props => props.column.id,
            header: () => <span>Role</span>,
            filterFn: customFilterFn
          },
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              <Actions info={ info.row.original } />
            ),
            footer: props => props.column.id,
            header: () => <span>Actions</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  const User = ({ info }) => {
    return (
      <div className='msync-table-user-column'>
        <span>{ info.user.trim().length !== 0 ? info.user : info.displayName }</span>
        <span>{ info.email }</span>
      </div>
    )
  }

  const Actions = ({ info }) => {
    return (
      <div className='msync-table-action-column'>
        { ( teamData.can_set_managers || teamData.team_type !== 'msynclicenses' ) && ( info.role === 'manager' && 
          <MembersTableButton
            btnName='Demote to member'
            id={ modalConfirmationMessageId}
            info={{ ...info, modal: modalMembersTblBtnId.demoteToMember }}
            teamType={teamData.team_type}
          /> )
        }
        { ( teamData.can_set_managers || teamData.team_type !== 'msynclicenses' ) && ( info.role === 'member' && 
          <MembersTableButton
            btnName='Set as manager'
            id={ modalConfirmationMessageId}
            info={{ ...info, modal: modalMembersTblBtnId.setAsManager }}
            teamType={teamData.team_type}
          /> )
        }
        { ( teamData.can_edit_members || teamData.team_type !== 'msynclicenses' ) && <MembersTableButton
          btnName='Manage'
          id={ modalManageUserId }
          info={ info }
          teamType={teamData.team_type}
        /> }
        { ( teamData.can_remove_members || teamData.team_type !== 'msynclicenses' ) && <MembersTableButton
          btnName='Delete'
          btnClassName='msync-button--is-theme-destructive'
          id={ modalConfirmationMessageId }
          info={{ ...info, modal: modalMembersTblBtnId.deleteUser }}
          teamType={teamData.team_type}
        /> }
      </div>
    )
  }

  const exportHeaders = getExportHeaders('team-members')
  const { teamData } = useContext(TeamContext)

  return (
    <>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='Members Table'
        context='Members'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
      >
        <BulkModalConfirmation key='modal-member-bulk-confirmation-message' />
        <ModalManageUser key='modal-member-manage-user'/>
        <ModalConfirmation key='modal-member-confirmation-message'/>
        <ModalInviteLink />
        {
          ! notAllowedUserMembershipStatus.includes(teamData.current_user_membership?.membership_status) &&
          <AddMemberModal
            teamId={ teamId }
            isAdmin={ teamData.is_user_admin }
            modalOption={ membersData.members_modal_option }
            refetchAdded={ refetchMembers }
            refetchInvited={ refetchMembers }
          />
        }
      </TanstackContent>
    </>
  )
}

export default MembersTable
